import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowRight } from 'lucide-react';
import type { WizardState } from '../../hooks/useSessionWizard';

interface Props {
  wizard: {
    state: WizardState;
    updateChecklist: (key: string, value: boolean) => void;
    isChecklistComplete: () => boolean;
    advanceToMaterials: () => Promise<void>;
    DEFAULT_CHECKLIST_KEYS: readonly string[];
  };
}

const CHECKLIST_LABELS: Record<string, string> = {
  contraindications_checked: 'Contre-indications vérifiées',
  allergies_checked:         'Allergies vérifiées',
  treatments_checked:        'Traitements en cours vérifiés',
  risks_explained:           'Risques expliqués au client',
  postcare_explained:        'Soins post-acte expliqués',
  new_needles_confirmed:     'Aiguilles neuves confirmées',
  workspace_disinfected:     'Espace de travail désinfecté',
  gloves_on:                 'Gants portés',
};

export default function Step4Checklist({ wizard }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { checklist } = wizard.state;

  const handleNext = async () => {
    if (!wizard.isChecklistComplete()) return;
    setLoading(true);
    setError(null);
    try {
      await wizard.advanceToMaterials();
    } catch (err: any) {
      setError(err?.message ?? 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const allDone   = wizard.isChecklistComplete();
  const doneCount = wizard.DEFAULT_CHECKLIST_KEYS.filter((k) => checklist[k]).length;

  const handleCheckAll = () => {
    wizard.DEFAULT_CHECKLIST_KEYS.forEach((k) => wizard.updateChecklist(k, !allDone));
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold font-display mb-1">Checklist de sécurité</h2>
        <p className="text-sm text-muted-foreground">
          {doneCount}/{wizard.DEFAULT_CHECKLIST_KEYS.length} éléments validés
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      <div className="glass-card p-4 space-y-3">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCheckAll}
            className="text-xs text-primary hover:underline underline-offset-2"
          >
            {allDone ? 'Tout décocher' : 'Tout cocher'}
          </button>
        </div>
        {wizard.DEFAULT_CHECKLIST_KEYS.map((key) => (
          <label key={key} className="flex items-center gap-3 cursor-pointer">
            <Checkbox
              checked={!!checklist[key]}
              onCheckedChange={(checked) => wizard.updateChecklist(key, !!checked)}
              className="border-muted-foreground data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <span className={`text-sm ${checklist[key] ? 'text-foreground' : 'text-muted-foreground'}`}>
              {CHECKLIST_LABELS[key] ?? key}
            </span>
          </label>
        ))}
      </div>

      <Button
        className="w-full gap-2"
        onClick={handleNext}
        disabled={!allDone || loading}
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <ArrowRight size={16} />
        )}
        Valider et choisir les matériaux
      </Button>
    </div>
  );
}
